import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/currency";
import { FileDown, CheckCircle, FileText } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

interface CompanyInvoicesPanelProps {
  companyId: string;
  bookings: any[];
  onRefresh: () => void;
}

export default function CompanyInvoicesPanel({ companyId, bookings, onRefresh }: CompanyInvoicesPanelProps) {
  const { toast } = useToast();
  const { t, tx } = useI18n();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerFilter, setCustomerFilter] = useState<string>("");

  const completedWithInvoice = useMemo(
    () => bookings.filter((b: any) => b.status === "completed" && b.invoice_requested === true),
    [bookings]
  );

  const unpaidBookings = useMemo(
    () =>
      bookings.filter(
        (b: any) =>
          b.status === "completed" &&
          b.vendor_marked_paid_at == null &&
          (b.payment_method === "cash" || b.payment_method === "invoice")
      ),
    [bookings]
  );

  const invoiceNotEmitted = useMemo(() => {
    let list = bookings.filter(
      (b: any) => b.invoice_requested === true && b.invoice_emitted_at == null && b.status !== "cancelled"
    );
    if (dateFrom) list = list.filter((b: any) => b.scheduled_date >= dateFrom);
    if (dateTo) list = list.filter((b: any) => b.scheduled_date <= dateTo);
    if (customerFilter)
      list = list.filter(
        (b: any) =>
          (b.customer_email && b.customer_email.toLowerCase().includes(customerFilter.toLowerCase())) ||
          (b.customer_name && b.customer_name.toLowerCase().includes(customerFilter.toLowerCase()))
      );
    return list;
  }, [bookings, dateFrom, dateTo, customerFilter]);

  const exportCompletedInvoiceToCsv = () => {
    const headers = [
      t("vendor.invoices.col_date"),
      t("vendor.drawer.start_time"),
      t("vendor.invoices.col_customer"),
      "Email",
      t("vendor.invoices.col_service"),
      "Address",
      t("vendor.invoices.col_amount"),
      "Notes",
    ];
    const rows = completedWithInvoice.map((b: any) => [
      b.scheduled_date || "",
      b.scheduled_time || "",
      b.customer_name || "",
      b.customer_email || "",
      b.service_name || "",
      b.address || (b.service_address_text || ""),
      String(b.total_amount ?? ""),
      b.notes || "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lavori-fattura-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: t("vendor.invoices.export_done"), description: t("vendor.invoices.export_done_desc") });
  };

  const markPaid = async (bookingId: string) => {
    try {
      const { error } = await (supabase as any).rpc("company_mark_booking_paid", { p_booking_id: bookingId });
      if (error) throw error;
      onRefresh();
      toast({ title: t("vendor.invoices.marked_paid") });
    } catch (e: any) {
      toast({ title: t("vendor.toast.error"), description: e?.message || t("vendor.invoices.op_failed"), variant: "destructive" });
    }
  };

  const markInvoiceEmitted = async (bookingId: string) => {
    try {
      const { error } = await (supabase as any).rpc("company_mark_booking_invoice_emitted", { p_booking_id: bookingId });
      if (error) throw error;
      onRefresh();
      toast({ title: t("vendor.invoices.marked_emitted") });
    } catch (e: any) {
      toast({ title: t("vendor.toast.error"), description: e?.message || t("vendor.invoices.op_failed"), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t("vendor.invoices.completed_title")}
          </CardTitle>
          <CardDescription>{t("vendor.invoices.completed_desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {completedWithInvoice.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("vendor.invoices.completed_empty")}</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 mb-4">
                <span className="text-sm text-muted-foreground">
                  {tx("vendor.invoices.bookings_count", { count: completedWithInvoice.length })}
                </span>
                <Button variant="outline" size="sm" onClick={exportCompletedInvoiceToCsv}>
                  <FileDown className="h-4 w-4 mr-2" />
                  {t("vendor.invoices.export_csv")}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">{t("vendor.invoices.col_date")}</th>
                      <th className="text-left py-2">{t("vendor.invoices.col_customer")}</th>
                      <th className="text-left py-2">{t("vendor.invoices.col_service")}</th>
                      <th className="text-right py-2">{t("vendor.invoices.col_amount")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedWithInvoice.slice(0, 20).map((b: any) => (
                      <tr key={b.id} className="border-b border-gray-100">
                        <td className="py-2">{b.scheduled_date} {b.scheduled_time}</td>
                        <td className="py-2">{b.customer_name || b.customer_email}</td>
                        <td className="py-2">{b.service_name}</td>
                        <td className="py-2 text-right">{formatCurrency(Number(b.total_amount || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {completedWithInvoice.length > 20 && (
                  <p className="text-xs text-muted-foreground mt-2">{t("vendor.invoices.showing_first")}</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            {t("vendor.invoices.unpaid_title")}
          </CardTitle>
          <CardDescription>{t("vendor.invoices.unpaid_desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {unpaidBookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("vendor.invoices.unpaid_empty")}</p>
          ) : (
            <div className="space-y-2">
              {unpaidBookings.map((b: any) => (
                <div
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 border rounded-lg"
                >
                  <div>
                    <span className="font-medium">{b.booking_number || b.id.slice(0, 8)}</span>
                    <span className="text-muted-foreground ml-2">{b.scheduled_date} – {b.customer_name || b.customer_email}</span>
                    <span className="ml-2">{formatCurrency(Number(b.total_amount || 0))}</span>
                    <Badge variant="secondary" className="ml-2">
                      {b.payment_method === "invoice" ? t("vendor.invoices.payment_invoice") : t("vendor.invoices.payment_cash")}
                    </Badge>
                  </div>
                  <Button size="sm" onClick={() => markPaid(b.id)}>
                    {t("vendor.invoices.mark_paid")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t("vendor.invoices.to_emit_title")}
          </CardTitle>
          <CardDescription>{t("vendor.invoices.to_emit_desc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">{t("vendor.invoices.date_from")}</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">{t("vendor.invoices.date_to")}</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">{t("vendor.invoices.customer_search")}</Label>
              <Input
                placeholder={t("vendor.invoices.search_ph")}
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="h-9 w-48"
              />
            </div>
          </div>
          {invoiceNotEmitted.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("vendor.invoices.to_emit_empty")}</p>
          ) : (
            <div className="space-y-2">
              {invoiceNotEmitted.map((b: any) => (
                <div
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-2 p-3 border rounded-lg"
                >
                  <div>
                    <span className="font-medium">{b.booking_number || b.id.slice(0, 8)}</span>
                    <span className="text-muted-foreground ml-2">{b.scheduled_date} – {b.customer_name || b.customer_email}</span>
                    <span className="ml-2">{formatCurrency(Number(b.total_amount || 0))}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => markInvoiceEmitted(b.id)}>
                    {t("vendor.invoices.mark_emitted")}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
